import type { AppLang } from './ClientApp';
import { useCallback, useEffect, useState } from 'react';
import { MembershipCard, type AppMembership } from './Membership';
import { PhoneSignIn } from './PhoneSignIn';
import { Reschedule } from './Reschedule';

/**
 * «My bookings» in the client app (owner 22.10.2026, App Store): sign in with a 6-digit code sent
 * to the e-mail the studio has, then the upcoming bookings. Changes go through WhatsApp.
 */
export interface MyBooking {
  start: string;
  durationMin: number;
  services: string[];
  staffName?: string;
  studio: string;
  status: 'waiting' | 'confirmed' | 'arrived';
  confirmUrl?: string;
  /** `<location>:<booking id>` for the reschedule calls. */
  id: string;
  /** May be moved in the app (more than 6 h before; same therapist). */
  canReschedule?: boolean;
}

const TOKEN_KEY = 'hub-me-token';
const EMAIL_KEY = 'hub-me-email';
const WHATSAPP = 'https://wa.me/351915311522';
const EN_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PT_DAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PT_MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

type State =
  | { kind: 'email'; busy: boolean; error?: string }
  | { kind: 'code'; email: string; busy: boolean; error?: string; resent?: boolean }
  | { kind: 'loading' }
  | { kind: 'list'; bookings: MyBooking[]; demo: boolean }
  | { kind: 'failed' };

interface ApiBody {
  data?: { token?: string; bookings?: MyBooking[]; demo?: boolean };
  error?: { code?: string };
}

function read(key: string): string | undefined {
  try {
    return window.localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function write(key: string, value?: string): void {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // storage blocked: the client signs in again next time
  }
}

/** «Sat 25 Oct, 10:10» / «sáb, 25 out, 10:10» — the booking's own local time, as in the CRM. */
export function bookingWhen(start: string): { en: string; pt: string } {
  const [y, m, d] = start.slice(0, 10).split('-').map(Number) as [number, number, number];
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const time = start.slice(11, 16);
  return {
    en: `${EN_DAYS[wd]} ${d} ${EN_MONTHS[m - 1]}, ${time}`,
    pt: `${PT_DAYS[wd]}, ${d} ${PT_MONTHS[m - 1]}, ${time}`,
  };
}

const ERRORS: Record<string, string> = {
  EMAIL_INVALID: 'Please check the e-mail address. · Verifique o e-mail.',
  CODE_INVALID: 'The code is not right or has expired. · O código não está certo ou expirou.',
  TOO_MANY_REQUESTS: 'Too many attempts — try again in a few minutes. · Demasiadas tentativas.',
};
const FALLBACK_ERROR = 'Something went wrong — please try again. · Algo correu mal — tente de novo.';

/** Stable default (a new function each render would reload the list in a loop). */
const browserFetch: typeof fetch = (...a) => fetch(...a);

const SIGN_IN = {
  pt: {
    title: 'As minhas marcações',
    email: 'O seu e-mail',
    emailHint: 'Use o e-mail da sua marcação. Enviamos um código de 6 dígitos.',
    send: 'Enviar código',
    sending: 'A enviar…',
    noEmail: 'Sem e-mail connosco? Escreva-nos no',
    codeTitle: 'Introduza o código',
    codeHint: (email: string) => `Se ${email} tiver uma marcação, o código está a caminho (veja também o spam).`,
    code: 'Código de 6 dígitos',
    resent: 'Novo código enviado.',
    show: 'Ver as minhas marcações',
    checking: 'A verificar…',
    otherEmail: 'Outro e-mail',
    again: 'Reenviar',
  },
  en: {
    title: 'My bookings',
    email: 'Your e-mail',
    emailHint: 'Use the e-mail you gave when booking. We will send you a 6-digit code.',
    send: 'Send code',
    sending: 'Sending…',
    noEmail: 'No e-mail with us? Message us on',
    codeTitle: 'Enter the code',
    codeHint: (email: string) => `If ${email} is the e-mail of a booking, a code is on its way (check spam too).`,
    code: '6-digit code',
    resent: 'New code sent.',
    show: 'Show my bookings',
    checking: 'Checking…',
    otherEmail: 'Other e-mail',
    again: 'Send again',
  },
  fr: {
    title: 'Mes rendez-vous',
    email: 'Votre e-mail',
    emailHint: 'Utilisez l’e-mail de votre réservation. Nous vous envoyons un code à 6 chiffres.',
    send: 'Envoyer le code',
    sending: 'Envoi…',
    noEmail: 'Pas d’e-mail chez nous ? Écrivez-nous sur',
    codeTitle: 'Saisissez le code',
    codeHint: (email: string) => `Si ${email} correspond à une réservation, le code arrive (vérifiez aussi le spam).`,
    code: 'Code à 6 chiffres',
    resent: 'Nouveau code envoyé.',
    show: 'Voir mes rendez-vous',
    checking: 'Vérification…',
    otherEmail: 'Autre e-mail',
    again: 'Renvoyer',
  },
  es: {
    title: 'Mis citas',
    email: 'Su e-mail',
    emailHint: 'Use el e-mail de su reserva. Le enviamos un código de 6 dígitos.',
    send: 'Enviar código',
    sending: 'Enviando…',
    noEmail: '¿Sin e-mail con nosotros? Escríbanos por',
    codeTitle: 'Introduzca el código',
    codeHint: (email: string) => `Si ${email} tiene una reserva, el código está en camino (mire también el spam).`,
    code: 'Código de 6 dígitos',
    resent: 'Nuevo código enviado.',
    show: 'Ver mis citas',
    checking: 'Comprobando…',
    otherEmail: 'Otro e-mail',
    again: 'Reenviar',
  },
  ru: {
    title: 'Мои записи',
    email: 'Ваш e-mail',
    emailHint: 'Укажите e-mail, который вы оставляли при записи. Мы пришлём код из 6 цифр.',
    send: 'Отправить код',
    sending: 'Отправляем…',
    noEmail: 'Нет e-mail у нас? Напишите нам в',
    codeTitle: 'Введите код',
    codeHint: (email: string) => `Если на ${email} есть запись, код уже в пути (проверьте и спам).`,
    code: 'Код из 6 цифр',
    resent: 'Новый код отправлен.',
    show: 'Показать мои записи',
    checking: 'Проверяем…',
    otherEmail: 'Другой e-mail',
    again: 'Отправить ещё раз',
  },
} as const;

export function MyBookings({ fetchImpl = browserFetch, lang = 'en' }: { fetchImpl?: typeof fetch; lang?: AppLang }) {
  const [state, setState] = useState<State>(() =>
    read(TOKEN_KEY) ? { kind: 'loading' } : { kind: 'email', busy: false },
  );
  const [email, setEmail] = useState(() => read(EMAIL_KEY) ?? '');
  const [moving, setMoving] = useState<MyBooking>();
  const [moved, setMoved] = useState<string>();
  const [code, setCode] = useState('');
  // Owner 27.10.2026: sign in by SMS when the server has Twilio; e-mail stays one tap away.
  const [sms, setSms] = useState(false);
  const [useEmail, setUseEmail] = useState(false);
  useEffect(() => {
    if (read(TOKEN_KEY)) return;
    let live = true;
    fetchImpl('/v1/me/config')
      .then((r) => (r.ok ? r.json() : undefined))
      .then((b: { data?: { sms?: boolean } } | undefined) => {
        if (live) setSms(b?.data?.sms === true);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [fetchImpl]);

  const post = useCallback(
    async (path: string, body: unknown): Promise<{ ok: boolean; body: ApiBody }> => {
      const r = await fetchImpl(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return { ok: r.ok, body: (await r.json().catch(() => ({}))) as ApiBody };
    },
    [fetchImpl],
  );

  const load = useCallback(async () => {
    const token = read(TOKEN_KEY);
    if (!token) return setState({ kind: 'email', busy: false });
    setState({ kind: 'loading' });
    try {
      const r = await fetchImpl('/v1/me/bookings', { headers: { Authorization: `Bearer ${token}` } });
      if (r.status === 401) {
        write(TOKEN_KEY);
        return setState({ kind: 'email', busy: false });
      }
      const body = (await r.json().catch(() => ({}))) as ApiBody;
      if (!r.ok) return setState({ kind: 'failed' });
      setState({ kind: 'list', bookings: body.data?.bookings ?? [], demo: body.data?.demo === true });
    } catch {
      setState({ kind: 'failed' });
    }
  }, [fetchImpl]);

  useEffect(() => {
    if (read(TOKEN_KEY)) void load();
  }, [load]);

  const sendCode = async (resend = false) => {
    const e = email.trim();
    setState(resend ? { kind: 'code', email: e, busy: true } : { kind: 'email', busy: true });
    try {
      const r = await post('/v1/me/code', { email: e });
      if (!r.ok) {
        const msg = ERRORS[r.body.error?.code ?? ''] ?? FALLBACK_ERROR;
        return setState(
          resend ? { kind: 'code', email: e, busy: false, error: msg } : { kind: 'email', busy: false, error: msg },
        );
      }
      write(EMAIL_KEY, e);
      setCode('');
      setState({ kind: 'code', email: e, busy: false, resent: resend });
    } catch {
      setState({ kind: 'email', busy: false, error: FALLBACK_ERROR });
    }
  };

  const signIn = async (forEmail: string) => {
    setState({ kind: 'code', email: forEmail, busy: true });
    try {
      const r = await post('/v1/me/session', { email: forEmail, code: code.trim() });
      if (!r.ok || !r.body.data?.token) {
        return setState({
          kind: 'code',
          email: forEmail,
          busy: false,
          error: ERRORS[r.body.error?.code ?? ''] ?? FALLBACK_ERROR,
        });
      }
      write(TOKEN_KEY, r.body.data.token);
      await load();
    } catch {
      setState({ kind: 'code', email: forEmail, busy: false, error: FALLBACK_ERROR });
    }
  };

  const signOut = () => {
    write(TOKEN_KEY);
    setState({ kind: 'email', busy: false });
  };

  // Owner 26.10.2026: the sign-in screens in the app style (illustration, one language).
  const L = SIGN_IN[lang];
  if (state.kind === 'email' && sms && !useEmail) {
    return (
      <PhoneSignIn
        lang={lang}
        fetchImpl={fetchImpl}
        onToken={(token) => {
          write(TOKEN_KEY, token);
          void load();
        }}
        onEmail={() => setUseEmail(true)}
      />
    );
  }
  if (state.kind === 'email') {
    return (
      <section className="ca-page" aria-label="My bookings">
        <div className="ca3-hero">
          <img src="/v3/t-visits.png" alt="" />
          <h1 className="ca3-h1">{L.title}</h1>
        </div>
        <form
          className="ca3-form"
          onSubmit={(ev) => {
            ev.preventDefault();
            void sendCode();
          }}
        >
          <label htmlFor="me-email">
            {L.email}
            <input
              id="me-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </label>
          <p className="ca3-hint">{L.emailHint}</p>
          {state.error && (
            <p className="ca3-error" role="alert">
              {state.error}
            </p>
          )}
          <button className="ca3-btn red" disabled={state.busy || !email.trim()}>
            {state.busy ? L.sending : L.send}
          </button>
        </form>
        <p className="ca3-hint ca-pad">
          {L.noEmail} <a href={WHATSAPP}>WhatsApp</a>
        </p>
      </section>
    );
  }

  if (state.kind === 'code') {
    return (
      <section className="ca-page" aria-label="My bookings">
        <div className="ca3-hero">
          <img src="/v3/phone.png" alt="" />
          <h1 className="ca3-h1">{L.codeTitle}</h1>
        </div>
        <form
          className="ca3-form"
          onSubmit={(ev) => {
            ev.preventDefault();
            void signIn(state.email);
          }}
        >
          <p className="ca3-hint">{L.codeHint(state.email)}</p>
          <label htmlFor="me-code">
            {L.code}
            <input
              id="me-code"
              className="ca3-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </label>
          {state.error && (
            <p className="ca3-error" role="alert">
              {state.error}
            </p>
          )}
          {state.resent && !state.error && <p className="ca3-hint">{L.resent}</p>}
          <button className="ca3-btn red" disabled={state.busy || code.length !== 6}>
            {state.busy ? L.checking : L.show}
          </button>
        </form>
        <div className="ca-row ca-pad">
          <button className="ca3-link" onClick={() => setState({ kind: 'email', busy: false })}>
            ← {L.otherEmail}
          </button>
          <button className="ca3-link" disabled={state.busy} onClick={() => void sendCode(true)}>
            {L.again}
          </button>
        </div>
      </section>
    );
  }

  if (state.kind === 'loading') {
    return (
      <section className="ca-page" aria-label="My bookings" aria-busy="true">
        <h1 className="ca-h1">
          My bookings <small>As minhas marcações</small>
        </h1>
        <p className="ca-hint ca-pad">Loading… · A carregar…</p>
      </section>
    );
  }

  if (state.kind === 'failed') {
    return (
      <section className="ca-page" aria-label="My bookings">
        <h1 className="ca-h1">
          My bookings <small>As minhas marcações</small>
        </h1>
        <div className="ca-card">
          <p className="ca-error" role="alert">
            We cannot load your bookings right now. · Não foi possível carregar as marcações.
          </p>
          <button className="ca-btn ca-btn-dark" onClick={() => void load()}>
            <b>Try again</b>
            <span>Tentar de novo</span>
          </button>
        </div>
      </section>
    );
  }

  const token = read(TOKEN_KEY);
  if (moving && token) {
    return (
      <Reschedule
        booking={moving}
        token={token}
        fetchImpl={fetchImpl}
        onCancel={() => setMoving(undefined)}
        onDone={(start) => {
          setMoving(undefined);
          setMoved(start);
          void load();
        }}
      />
    );
  }

  return (
    <section className="ca-page" aria-label="My bookings">
      <h1 className="ca-h1">
        My bookings <small>As minhas marcações</small>
      </h1>
      {moved && (
        <p className="ca-done ca-pad" role="status">
          Moved to {bookingWhen(moved).en}. · Mudada para {bookingWhen(moved).pt}.
        </p>
      )}
      {state.demo && <p className="ca-hint ca-pad">Demo account — sample bookings. · Conta de demonstração.</p>}
      {token && <MyMemberships token={token} fetchImpl={fetchImpl} lang={lang} />}
      {state.bookings.length === 0 ? (
        <div className="ca-card">
          <p>
            No upcoming bookings.
            <br />
            <span className="ca-muted">Sem marcações futuras.</span>
          </p>
          <a className="ca-btn ca-btn-red" href="/app">
            <b>Book a treatment</b>
            <span>Marcar tratamento</span>
          </a>
        </div>
      ) : (
        <ul className="ca-list">
          {state.bookings.map((b) => {
            const w = bookingWhen(b.start);
            return (
              <li key={b.start + b.services.join()} className="ca-card ca-booking">
                <div className="ca-when">
                  <b>{w.en}</b>
                  <span>{w.pt}</span>
                </div>
                <div className="ca-what">{b.services.join(' + ')}</div>
                <div className="ca-muted">
                  {b.staffName ? `${b.staffName} · ` : ''}
                  {b.studio} · {b.durationMin} min
                </div>
                <div className="ca-row">
                  <span className={`ca-status ca-status-${b.status}`}>
                    {b.status === 'confirmed'
                      ? 'Confirmed · Confirmada'
                      : b.status === 'arrived'
                        ? 'Checked in · Presente'
                        : 'Not confirmed · Por confirmar'}
                  </span>
                  {b.confirmUrl && (
                    <a className="ca-chip" href={b.confirmUrl}>
                      Confirm · Confirmar
                    </a>
                  )}
                  {b.canReschedule && (
                    <button className="ca-chip ca-chip-light" onClick={() => setMoving(b)}>
                      Change time · Alterar hora
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="ca-hint ca-pad">
        To change or cancel, <a href={WHATSAPP}>message us on WhatsApp</a> (at least 6 h before).
        <br />
        Para alterar ou cancelar, escreva-nos no WhatsApp (pelo menos 6 h antes).
      </p>
      <div className="ca-row ca-pad">
        <button className="ca-link" onClick={() => void load()}>
          ↻ Refresh · Atualizar
        </button>
        <button className="ca-link" onClick={signOut}>
          Sign out · Sair
        </button>
      </div>
    </section>
  );
}

/**
 * Memberships from Altegio at the top of «My bookings» (owner 23.10.2026): name, sessions left,
 * valid until. Read-only; shows nothing while loading, without memberships or on error.
 */
export function MyMemberships({
  token,
  fetchImpl,
  lang = 'en',
}: {
  token: string;
  fetchImpl: typeof fetch;
  lang?: AppLang;
}) {
  const [list, setList] = useState<AppMembership[]>([]);
  useEffect(() => {
    let live = true;
    fetchImpl('/v1/me/memberships', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : undefined))
      .then((b: { data?: { memberships?: AppMembership[] } } | undefined) => {
        if (live) setList(b?.data?.memberships ?? []);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [token, fetchImpl]);
  if (list.length === 0) return null;
  // owner 27.10.2026: the same dark card with the brand mark as on the home screen
  return (
    <div className="ca-members ca3-list" aria-label="My memberships">
      {list.map((m, i) => (
        <MembershipCard key={m.id ?? i} m={m} lang={lang} />
      ))}
    </div>
  );
}
